<template>
    <div v-if="permissions && $getPermission(permissions,21).is_granted">
        <div class="mt-2 flex justify-between items-center text-textOnBkg dark:text-dark.textOnBkg mr-2">
            <MazBtn size="sm" v-if="permissions && $getPermission(permissions,21).is_granted" 
                @click="payrollSettingsModal = true" class="z-10 ml-3 sm:ml-24">
                <font-awesome-icon icon="fa-solid fa-gears" /> {{ $t('Settings') }}
            </MazBtn>
            <div v-else></div>
            <MazPicker
                v-model="dRange"
                size="sm"
                pickerPosition="right"
                :label="$t('select_period')"
                :max-date="new Date().toISOString().split('T')[0]"
                double
            />
        </div>

        <transition v-if="permissions && $getPermission(permissions,5).is_granted" name="zoom-fade">
            <section v-if="payrollSettingsModal" @click="payrollSettingsModal = false" class="backdrop-blur w-full h-full fixed inset-0 bg-gray-700 bg-opacity-30 z-50 flex justify-center items-center px-4 text-black">
                <section @click.stop class="bg-white rounded-lg w-full max-h-screen max-w-md sm:max-w-5xl mx-auto my-auto p-8 sm:p-12 z-50 overflow-auto relative">
                    <font-awesome-icon @click="payrollSettingsModal = false" icon="fas fa-xmark" class="transition absolute p-6 top-0 right-0 text-xl cursor-pointer hover:opacity-50" />
                    <payroll-settings :noarrow="true" @close="handleClose" />
                </section>
            </section>
        </transition>

        <section class="sm:ml-24 mx-2 sm:mx-0 mt-2 mb-5 bg-squares dark:bg-dark.tableBG text-panelsText dark:text-dark.panelsText p-1 px-7 rounded-xl shadow-light dark:shadow-dark">
            <h1 class="m-5 ml-0 font-bold text-xl">{{ $t('Payroll') }}</h1>
            
            <span v-if="!loading && sextarSalaries.length == 0" class="text-black/80 dark:text-dark.textOnBkg">
                <h2 class="font-bold">{{ $t('no_payroll[0]') }}</h2>
                <div v-html="$t('no_payroll[1]')"></div>
            </span>
            <div class="flex items-center justify-between mt-4">
                <MazBtn size="sm" @click="$router.push('sexting')" class="z-10 bg-growth text-white rounded p-1.5 px-5 cursor-pointer">
                    {{ $t('shifts_earnings_link') }} <font-awesome-icon icon="fa-solid fa-arrow-right" />
                </MazBtn>
                <MazBtn size="sm" class="bg-button text-buttonText ml-2 rounded-lg p-1.5 px-3 cursor-pointer" @click="exportToCSV">
                    <span ref="exportLoading" class="hidden"><font-awesome-icon icon="fas fa-spinner" class="animate-spin mr-2" /></span>{{ $t('export_to_csv') }}
                </MazBtn>
            </div>

            <div ref="tableRef" class="mt-3 pb-5 overflow-auto">
                <table class="w-full border-collapse">
                    <thead class="bg-gray-100 dark:bg-dark.panels w-full p-3 border border-sepBorder dark:border-dark.sepBorder sm:rounded-t-lg text-left font-semibold text-gray-500 dark:text-dark.textOnBkg">
                        <tr>
                            <th class="border-r border-sepBorder dark:border-dark.sepBorder">{{ $t('name') }}</th>
                            <th class="border-r border-sepBorder dark:border-dark.sepBorder">{{ $t('team_position') }}</th>
                            <th class="border-r border-sepBorder dark:border-dark.sepBorder">{{ $t('from') }}</th>
                            <th class="border-r border-sepBorder dark:border-dark.sepBorder">{{ $t('to') }}</th>
                            <th class="border-r border-sepBorder dark:border-dark.sepBorder">{{ $t('cost_ratio') }}</th>
                            <th class="border-r border-sepBorder dark:border-dark.sepBorder">{{ $t('salary_management[5]') }}</th>
                            <th class="border-r border-sepBorder dark:border-dark.sepBorder">{{ $t('salary_with_bonus') }}</th>
                            <th class="border-r border-sepBorder dark:border-dark.sepBorder" v-if="permissions && $getPermission(permissions,23).is_granted">{{ $t('paid') }}</th>
                            <th v-if="permissions && $getPermission(permissions,23).is_granted">{{ $t('actions') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr class="border border-sepBorder dark:border-dark.sepBorder" v-for="(salary, index) in sextarSalaries" :key="index">
                            <td class="border-r border-sepBorder dark:border-dark.sepBorder">{{ salary.data.emplData.emplName }}</td>
                            <td class="border-r border-sepBorder dark:border-dark.sepBorder">{{ salary.data.emplData.team }} - {{ salary.data.emplData.positionName }}</td>
                            <td class="border-r border-sepBorder dark:border-dark.sepBorder">{{ salary.timestamp_from }}</td>
                            <td class="border-r border-sepBorder dark:border-dark.sepBorder">{{ salary.timestamp_to }}</td>
                            <td class="border-r border-sepBorder dark:border-dark.sepBorder">{{ ((salary.data.shifts.reduce((totalSalary, shift) => totalSalary + shift.salary, 0) / salary.data.shifts.reduce((totalEarnings, shift) => totalEarnings + shift.models.reduce((sum, model) => sum + parseFloat(model.earningsSum), 0), 0)) * 100).toFixed(2) }} %</td>
                            <td class="border-r border-sepBorder dark:border-dark.sepBorder">$ {{ salary.correction ?? 0 }}</td>
                            <td class="border-r border-sepBorder dark:border-dark.sepBorder">$ {{ String(parseFloat(parseFloat(salary.data.shifts.reduce((totalSalary, shift) => totalSalary + shift.salary, 0)).toFixed(2) + parseFloat(salary.correction ?? 0)).toFixed(2).toLocaleString('en-US')).replace(/,/g, ' ') }}</td>
                            <td class="border-r border-sepBorder dark:border-dark.sepBorder" v-if="permissions && $getPermission(permissions,23).is_granted">
                                <font-awesome-icon @click="markAsPaid(salary.id, index, salary.paid)" :icon="xcheckIcon(salary.paid)" class="text-2xl cursor-pointer" :class="salary.paid == 1 ? 'text-growth' : 'text-gray-400'"></font-awesome-icon>
                            </td>
                            <td class="border-r border-sepBorder dark:border-dark.sepBorder cursor-pointer" v-if="permissions && $getPermission(permissions,23).is_granted" @click="removeLock(salary, index)">
                                <div class="bg-decline bg-opacity-20 text-decline px-2 w-max rounded">{{ $t('delete') }}</div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </section>

        <section class="sm:ml-24 mx-2 sm:mx-0 mt-4 mb-10 pb-10 bg-squares dark:bg-dark.tableBG text-panelsText dark:text-dark.panelsText p-1 px-7 rounded-xl max-w-7xl shadow-light dark:shadow-dark">
            <h1 class="m-5 ml-0 font-bold text-xl">{{ $t('payroll.h_main')}}:</h1>
            <div class="grid grid-cols-2 gap-5 items-center">
                <img :src="payroll_illustration_1" class="w-full rounded-lg mx-auto max-w-xl shadow-light dark:shadow-dark" />
                <div class="mx-auto">
                    <h2 class="font-bold text-xl mb-1">{{ $t('payroll.h1') }}</h2>
                    <p class="max-w-2xl">{{ $t('payroll.p1') }}</p>
                    <br />
                    <h2 class="font-bold text-xl mb-1">{{ $t('payroll.h2') }}</h2>
                    <p class="max-w-2xl">{{ $t('payroll.p2') }}</p>
                </div>
            </div>
            <hr class="w-4/5 border-0 border-t border-sepBorder dark:border-dark.sepBorder my-10 mx-auto" noshade="noshade" />
            <div class="grid grid-cols-2 gap-5 items-center">
                <div class="mx-auto">
                    <h2 class="font-bold text-xl mb-1">{{ $t('payroll.h3') }}</h2>
                    <p class="max-w-2xl">{{ $t('payroll.p3') }}</p>
                    <br />
                    <h2 class="font-bold text-xl mb-1">{{ $t('payroll.h4') }}</h2>
                    <p class="max-w-2xl">{{ $t('payroll.p4') }} <u class="cursor-pointer" @click="$router.push('sextingCosts')">{{ $t('Sexting_costs') }}</u> section</p>
                </div>
                <div><img :src="payroll_illustration_2" class="w-full rounded-lg mx-auto max-w-xl shadow-light dark:shadow-dark" /></div>
            </div>
        </section>
    </div>
    <div v-else class="text-center mt-52 text-textOnBkg">{{ $t("no_permission") }}</div>
</template>

<script setup>
    import payrollSettings from "@/components/settings/salary.vue";
    import { getFinance } from '@/api/sexting.js';
    import payroll_illustration_1 from '@/assets/images/payroll_illustration_1.svg';
    import payroll_illustration_2 from '@/assets/images/payroll_illustration_2.svg';
</script>

<script>
    import { useAuthStore } from '@/stores/auth';
    export default {
        data() {
            return {
                customPeriod: {
                    dateFrom: new Date().getFullYear() + '-' + (new Date().getMonth() + 1).toString().padStart(2, '0') + '-01',
                    timeFrom: {hours: 0, minutes: 0, seconds: 0},
                    dateTo: new Date().toISOString().split('T')[0],
                    timeTo: {hours: 23, minutes: 59, seconds: 59},
                    maxTo: new Date().toISOString().split('T')[0]
                },
                loading:false,
                sextarSalaries:[],
                sextingCosts: [],
                mounted:false,
                selectedMonth: null,
                selectedGroup: null,
                selectedEmpl: null,
                selectedTeam: null,
                payrollSettingsModal:false,
                dRange: {start: null, end: null},
                dateFrom: this.$moment().subtract(9, 'days').format('YYYY-MM-DD'),
                dateTo: this.$moment().format('YYYY-MM-DD'),
                fetchingSalaries:false,
            }
        },
        computed: {
            authStore() {
                return useAuthStore();
            },
            permissions() {
                return this.authStore.permissions;
            },
        },
        watch: {
            dRange: function (n,o) {
                this.dateFrom = n.start ? n.start : this.dateFrom;
                this.dateTo = n.end ? n.end : this.dateTo;

                if (this.dateFrom && this.dateTo) {
                    this.getSalaries();
                }
            }
        },
        methods: {
            exportToCSV() {
                this.$refs.exportLoading.classList.remove('hidden');

                setTimeout(() => {
                    const table = this.$refs.tableRef;
                    const data = [];

                    const rows = table.querySelectorAll('tr');
                    rows.forEach(row => {
                        const rowData = [];
                        row.querySelectorAll('td, th').forEach(cell => {
                            rowData.push(cell.innerText);
                        });
                        data.push(rowData.join(','));
                    });

                    // Create a CSV content string
                    const csvContent = 'data:text/csv;charset=utf-8,' + data.join('\n');

                    const encodedUri = encodeURI(csvContent);
                    const link = document.createElement('a');
                    link.setAttribute('href', encodedUri);
                    let filename = "payroll_" + this.$moment().format('YYYY-MM-DD') + ".csv";
                    link.setAttribute('download', filename);

                    setTimeout(() => {
                        this.$refs.exportLoading.classList.add('hidden');
                        link.click();
                    }, 1000);
                }, 2000);
            },
            removeLock(salary, index) {
                if (!this.permissions || !this.$getPermission(this.permissions,23).is_granted) {
                    return;
                }
                
                if (confirm("Unlock?")) {
                    this.$axios.post("finance/", {
                        action: "deleteSalary",
                        userToken: this.$cookies.get('userToken'),
                        companyID: this.$cookies.get('c_id'),
                        timestamp_from: salary.timestamp_from,
                        timestamp_to: salary.timestamp_to,
                        salaryID: salary.id
                    }).then((result) => {
                        if (result.status == 200 && result.data.success === true) {
                            this.sextarSalaries.splice(index, 1);
                        }
                    }, (error) => {
                        console.log(error);
                    });
                }
            },
            handleClose() {
                this.shiftSettingsModal = false;
            },
            updateSelectedMonth(month) {
                this.selectedMonth = month;
            },
            updateSelectedTeam(team) {
                this.selectedTeam = team;
            },
            updateSelectedGroup(group) {
                this.selectedGroup = group;
            },
            updateSelectedEmpl(empl) {
                this.selectedEmpl = empl;
            },
            xcheckIcon(condition) {
                return condition==1 ? 'fas fa-toggle-on' : 'fas fa-toggle-off';
            },
            markAsPaid(salary_id, salary_index, action) {
                let new_value = 0;
                let confirm_message = '';
                if (action == 0) {
                    new_value = 1;
                    confirm_message = "Mark as paid?";
                } else {
                    new_value = 0;
                    confirm_message = "Mark as unpaid?";
                }

                if (confirm(confirm_message)) {
                    this.$axios.post("finance/", {
                        userToken: this.$cookies.get('userToken'),
                        action:'markAsPaid',
                        new_value: new_value,
                        salaryID: salary_id,
                        companyID: this.$cookies.get('c_id')
                    }).then((result) => {
                        if (result.data.success == true) {
                            this.sextarSalaries[salary_index].paid = Boolean(new_value);
                        }
                    }, (error) => {
                        console.log(error);
                    });
                }
            },
            async getSalaries() {
                if (this.fetchingSalaries) { return; }
                this.fetchingSalaries = true;
                this.loading = true;
                this.sextarSalaries = await getFinance({
                    userToken: this.$cookies.get('userToken'),
                    build:'sextarSalaries',
                    timestamp_from: this.dateFrom + ' 00:00:00',
                    timestamp_to: this.dateTo + ' 00:00:00', 
                    companyID: this.$cookies.get('c_id')
                });
                this.loading = false;
                setTimeout(() => { this.fetchingSalaries = false; }, 500);
            },
        },
        mounted() {
            this.getSalaries();
            this.mounted = true;
        }
    }
</script>

<style scoped>
    tr th {
        padding: 0.5rem 0.5rem;
    }
    tr td {
        padding: 0.5rem 0.5rem;
    }
</style>